import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';

import CoparticipationService from '@/services-http/servicos/CoparticipationService';
import store from '@/store/index';
import i18n from '@/plugins/i18n';

import {
  buildFilterParams,
  defaultFilter,
  defaultPagination,
  initState,
  normalizeRecordsPage,
} from './coparticipation-factory';
import {
  CoparticipationFilter,
  CoparticipationModel,
  CoparticipationRecord,
  CoparticipationTableOptions,
  Modal,
} from './coparticipation-types';

let searchRequestSequence = 0;

const httpStatusFromError = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') return null;
  const { response } = error as { response?: { status?: number } };
  return typeof response?.status === 'number' ? response.status : null;
};

@Module({
  namespaced: true,
  name: 'coparticipation',
  dynamic: true,
  store,
})
class CoparticipationStore extends VuexModule {
  data: CoparticipationModel = initState;

  get filter() {
    return this.data.filter;
  }

  get records() {
    return this.data.records;
  }

  get loadingPage() {
    return this.data.loadingPage;
  }

  get loadingTable() {
    return this.data.loadingTable;
  }

  get loadingExport() {
    return this.data.loadingExport;
  }

  get pagination() {
    return this.data.pagination;
  }

  get showSnackbarCustomize() {
    return this.data.showSnackbarCustomize;
  }

  get hasSearched() {
    return this.data.hasSearched;
  }

  get canSearch() {
    const {
      carrierId,
      financialGroupId,
      contractId,
      competence,
    } = this.data.filter;

    return carrierId != null
      && financialGroupId != null
      && contractId != null
      && Boolean(competence);
  }

  get hasResults() {
    return this.data.records.length > 0;
  }

  get showTable() {
    return this.data.hasSearched || this.data.loadingTable;
  }

  @Action
  initPage() {
    this.RESET_FILTER_STATE();
    this.CLEAR_RESULTS();
  }

  @Action
  applyFilter(filter: Partial<CoparticipationFilter>) {
    this.SET_FILTER(filter);
    if (!this.canSearch) {
      this.CLEAR_RESULTS();
    }
  }

  @Action
  resetFilter() {
    this.RESET_FILTER_STATE();
    this.CLEAR_RESULTS();
  }

  @Action
  async searchByFilter() {
    if (!this.canSearch) {
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t('coparticipation.messages.requiredFilters')),
        show: true,
      });
      return;
    }

    this.SET_PAGINATION({ page: 1 });
    await this.search();
  }

  @Action
  async updateTableOptions(options: CoparticipationTableOptions) {
    if (!this.canSearch || !this.data.hasSearched) return;

    const nextPage = options?.page ?? this.data.pagination.page;
    const nextItemsPerPage = options?.itemsPerPage ?? this.data.pagination.itemsPerPage;
    const hasChanged = nextPage !== this.data.pagination.page
      || nextItemsPerPage !== this.data.pagination.itemsPerPage;

    if (!hasChanged) return;

    this.SET_PAGINATION({ page: nextPage, itemsPerPage: nextItemsPerPage });
    await this.search();
  }

  @Action
  async exportRecords() {
    if (!this.canSearch) {
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t('coparticipation.messages.requiredFilters')),
        show: true,
      });
      return false;
    }

    if (this.data.loadingExport) return false;

    this.SET_EXPORT_LOADING(true);

    try {
      const service = new CoparticipationService();
      const params = buildFilterParams(this.data.filter);
      await service.exportRecords(params);
      return true;
    } catch (error) {
      const status = httpStatusFromError(error);
      const messageKey = status === 403
        ? 'coparticipation.messages.forbiddenAccess'
        : 'coparticipation.messages.exportError';
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t(messageKey)),
        show: true,
      });
      return false;
    } finally {
      this.SET_EXPORT_LOADING(false);
    }
  }

  @Action
  private async search() {
    searchRequestSequence += 1;
    const currentSearchId = searchRequestSequence;
    const params = buildFilterParams(this.data.filter);
    const pageable = {
      page: this.data.pagination.page - 1,
      size: this.data.pagination.itemsPerPage,
    };

    this.SET_PAGE_LOADING(true);
    this.SET_TABLE_LOADING(true);
    this.SET_HAS_SEARCHED(true);

    try {
      const service = new CoparticipationService();
      const response = await service.fetchRecords(params, pageable);

      if (currentSearchId !== searchRequestSequence) return;

      const page = normalizeRecordsPage(response);
      this.SET_TOTAL_ITEMS(page.totalElements);
      this.SET_RECORDS(page.content);
    } catch (error) {
      if (currentSearchId !== searchRequestSequence) return;

      this.CLEAR_RECORDS();
      this.SET_TOTAL_ITEMS(0);
      const status = httpStatusFromError(error);
      const messageKey = status === 403
        ? 'coparticipation.messages.forbiddenAccess'
        : 'coparticipation.messages.searchError';
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t(messageKey)),
        show: true,
      });
    } finally {
      if (currentSearchId === searchRequestSequence) {
        this.SET_PAGE_LOADING(false);
        this.SET_TABLE_LOADING(false);
      }
    }
  }

  @Mutation
  showSnackbar(modalParams: Modal) {
    this.data.showSnackbarCustomize = modalParams;
    if (modalParams.show) {
      setTimeout(() => {
        this.data.showSnackbarCustomize = { type: null, message: '', show: false };
      }, 5000);
    }
  }

  @Mutation
  private SET_FILTER(filter: Partial<CoparticipationFilter>) {
    this.data.filter = { ...this.data.filter, ...filter };
  }

  @Mutation
  private RESET_FILTER_STATE() {
    this.data.filter = defaultFilter();
  }

  @Mutation
  private SET_PAGINATION(pagination: Partial<{ page: number; itemsPerPage: number; totalItems: number }>) {
    this.data.pagination = { ...this.data.pagination, ...pagination };
  }

  @Mutation
  private SET_PAGE_LOADING(loading: boolean) {
    this.data.loadingPage = loading;
  }

  @Mutation
  private SET_TABLE_LOADING(loading: boolean) {
    this.data.loadingTable = loading;
  }

  @Mutation
  private SET_EXPORT_LOADING(loading: boolean) {
    this.data.loadingExport = loading;
  }

  @Mutation
  private SET_HAS_SEARCHED(hasSearched: boolean) {
    this.data.hasSearched = hasSearched;
  }

  @Mutation
  private SET_TOTAL_ITEMS(total: number) {
    this.data.pagination.totalItems = total;
  }

  @Mutation
  private CLEAR_RECORDS() {
    this.data.records = [];
  }

  @Mutation
  private CLEAR_RESULTS() {
    this.data.records = [];
    this.data.pagination = defaultPagination();
    this.data.loadingExport = false;
    this.data.hasSearched = false;
  }

  @Mutation
  private SET_RECORDS(records: CoparticipationRecord[]) {
    this.data.records = records;
  }
}

export default getModule(CoparticipationStore);
